import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import useDialogModal from 'hooks/use-dialog-modal';
import { useOrderContext } from 'state/order';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { isCatering } from 'utils/service-mode';

import { ChangeServiceModeLinkContentsProps } from './types';

const messages = defineMessages({
  changeServiceMode: {
    id: 'changeServiceMode',
    defaultMessage: 'Change Service Mode',
    description: 'Change Service Mode modal heading'
  },
  cateringChangeServiceModeDescription: {
    id: 'cateringChangeServiceModeDescription',
    defaultMessage:
      'Exiting Catering service mode will result in your cart clearing. Continue changing service mode?',
    description: 'Description for exiting catering service mode'
  }
});

const ChangeServiceModeLinkContents: React.FC<ChangeServiceModeLinkContentsProps> = ({
  linkText,
  linkRoute,
  orderingEnabled
}) => {
  const { formatMessage } = useIntl();
  const { cartEntries, emptyCart, selectServiceMode } = useOrderContext();
  const { resetStore } = useStoreContext();
  const { serviceMode, setServiceMode } = useServiceModeContext();
  const navigate = useNavigate();
  const onConfirmStoreChange = useCallback(() => {
    selectServiceMode(null);
    resetStore();
    emptyCart();
    navigate(linkRoute);
  }, [emptyCart, linkRoute, navigate, resetStore, selectServiceMode]);
  const [ServiceChangeDialog, openServiceChangeDialog] = useDialogModal({
    onConfirm: onConfirmStoreChange,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Catering Service change'
  });
  // we need to set a default service mode to prevent
  // the service mode modal from popping up when we route to the store locator
  const setDefaultServiceMode = () => {
    setServiceMode(ServiceMode.TAKEOUT);
  };
  const handleClick = (event: React.SyntheticEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (isCatering(serviceMode) && cartEntries.length > 0) {
      openServiceChangeDialog();
    } else {
      if (!orderingEnabled && !serviceMode) {
        setDefaultServiceMode();
      }
      navigate(linkRoute);
    }
  };

  return (
    <>
      <Link
        aria-label={linkText}
        data-testid="restaurants-mobile"
        onClick={handleClick}
        to={linkRoute}
      >
        <b>{linkText}</b>
      </Link>
      <ServiceChangeDialog
        body={formatMessage(messages.cateringChangeServiceModeDescription)}
        heading={formatMessage(messages.changeServiceMode)}
      />
    </>
  );
};

export default ChangeServiceModeLinkContents;
